import { Link, createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import SearchBar from "#/components/searchbar";
import { toAppPath } from "#/lib/router-links";
import { metadataToHead, type PageMetadata } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <NavBar />
      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <h1 className="mb-10 px-5 text-center text-5xl">
          Search for Physician Jobs
        </h1>

        <section className="mx-8 space-y-5">
          <SearchBar value="" />
        </section>

        <section className="mx-8 space-y-5">
          <p>
            The Roamdoc job board is for medical doctors searching for career
            advancement opportunities.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Physician Jobs
          </h2>
          <p>
            Explore a comprehensive range of physician positions across leading
            healthcare facilities, tailored to suit your expertise and career
            aspirations. Discover opportunities that offer competitive salaries,
            professional growth, and the chance to work in diverse clinical
            environments, from bustling city hospitals to specialized surgical
            centers.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Top Cities for Physicians
          </h2>
          <ul>
            <li>
              <h4>
                <Link {...toAppPath("/physician-jobs-in-houston-tx")}>
                  Search physician jobs near Houston, TX
                </Link>
              </h4>
              <p>
                Nestled in the heart of the Lone Star State, Dallas, Texas is a
                thriving metropolis with state-of-the-art healthcare facilities
                like Baylor University Medical Center, University of Texas
                Southwestern Medical Center, and Methodist Dallas Medical
                Center.
              </p>
            </li>
          </ul>

          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Physician Job Seeker Career Resources
          </h2>
          <p>
            Unlock your potential with our comprehensive career resources,
            designed to guide you through every step of your job search. From
            resume building tips and interview techniques to industry insights
            and continuing education opportunities, we provide the tools you
            need to advance your career in this dynamic and rewarding medical
            field.
          </p>
          <h3>Salary Guides</h3>
          <p>
            Navigate the financial aspects of your MD career with our detailed
            salary guides, offering up-to-date information on compensation
            trends, regional variances, and factors influencing earning
            potential in the field.
          </p>
          <h3>Resume Examples</h3>
          <p>
            Enhance your resume writing skills with our tailored physician
            resume examples , offering inspiration and guidance to showcase your
            professional journey and expertise effectively.
          </p>
          <h3>Career Planning Portal</h3>
          <p>
            Access expert coaching and tailored career advice through our Career
            Planning Portal, designed to support your growth and success in the
            medical field with personalized strategies and professional
            insights.
          </p>
          <h3>Interview Tips</h3>
          <p>
            Master your physician job interviews with our expert tips, covering
            everything from common questions and effective communication
            strategies to showcasing your clinical expertise and understanding
            of the latest medical practices.
          </p>
          <h3>Create Job Alert</h3>
          <p>
            Set up personalized notifications for job alerts, and be instantly
            informed about the latest physician opportunities that align with
            your career goals and professional expertise.
          </p>
          <h3>Upload Resume</h3>
          <p>
            Maximize your career opportunities by{" "}
            <Link {...toAppPath("/upload-healthcare-resume")}>uploading your resume,</Link>{" "}
            allowing top healthcare employers to discover your skills and
            expertise.
          </p>
          <h3>Networking Events</h3>
          <p>
            Attend specialized networking events tailored for medical doctors ,
            offering unparalleled opportunities to connect with industry
            leaders, peers, and potential employers to expand your professional
            horizons.
          </p>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Employer Tools
          </h2>
          <p>
            Enhance your recruitment strategy with our comprehensive Roamdoc
            employer tools, enabling you to effortlessly search for candidates
            and connect with top-tier medical docters. Leverage targeted job
            postings, advanced candidate search features, and more to find the
            ideal match for your healthcare team, streamlining the hiring
            process.
          </p>
          <h3>Salary Guides</h3>
          <p>
            Access up-to-date salary guides to gain insights into competitive
            compensation trends in the all MD fields, helping you to attract and
            retain top talent with attractive and market-aligned salary
            offerings.
          </p>
          <h3>Resume Database</h3>
          <p>
            Access our extensive Resume Database to effortlessly search and
            discover a diverse pool of highly qualified candidates, making it
            easier to identify and reach out to the ideal candidiates.
          </p>
          <h3>Hiring Solutions Provider</h3>
          <p>
            As a leading job matching service, Roamdoc offers innovative and
            customizable tools to assist employers in attracting, evaluating,
            and retaining the best physician talent , ensuring a seamless and
            effective recruitment experience.
          </p>
          <h3>Online Recruitment Platform</h3>
          <p>
            Our online recruitment platform offers a user-friendly, efficient,
            and highly effective way for employers to post jobs, manage
            applications, and connect with a vast pool of qualified physicians.
          </p>
          <button
            type="button"
            className="rounded-lg bg-sky-500 px-4 py-2 text-center text-sm font-medium text-white hover:bg-sky-800 focus:outline-none focus:ring-4 focus:ring-blue-300"
          >
            Post a Job
          </button>
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Find Locum Tenens Assignments
          </h2>
          <p>
            Navigate the diverse opportunities in Locum Tenens, tailored for{" "}
            <Link {...toAppPath("/locum-work-physicians-guide")}>
              physicians seeking varied short-term assignments
            </Link>
            . Gain insights into how this flexible model can enhance your
            career, offering a harmonious blend of professional growth and
            personal freedom.
          </p>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={addJsonLd()}
      />
    </>
  );
}

export const metadata: PageMetadata = {
  title: "Search Physician (MD) Jobs Near You",
  description:
    "Find Physician jobs and hospitals hiring in the Texas, Florida, California, New York and all of U.S.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/physician-job-search`,
  },
};

function addJsonLd() {
  return {
    __html: `{
      "@context": "https://schema.org/",
      "@type": "WebPage",
      "url": "https://www.roamdoc.com/physician-job-search",
      "logo": "hhttps://www.roamdoc.com/roamdoc-logo.png",
      "inLanguage": "en-US",
      "description": "Job search engine for physicians in the United States."
    }`,
  };
}

export const Route = createFileRoute("/(job-search)/physician-job-search/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
